import { activeBirthEvents } from './birthCalculations';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { transportIssues } from './TransportEntry';
import type { ClinicalEvent, ClinicalSubject } from './types';
export function transportTimeline(payload: Record<string, unknown>) {
  const ems = payload.ems as Record<string, unknown> | undefined;
  const points = [
    ['Decision', payload.decision_at],
    ['Facility notified', payload.facility_notified_at],
    ['EMS called', ems?.called_at],
    ['EMS arrived', ems?.arrived_at],
    ['EMS departed', ems?.departed_at],
    ['Facility arrival', payload.arrived_facility_at],
  ]
    .filter(([, at]) => typeof at === 'string' && Number.isFinite(Date.parse(at as string)))
    .map(([label, at]) => ({ label: label as string, at: at as string }))
    .sort((a, b) => Date.parse(a.at) - Date.parse(b.at));
  const start = Date.parse(String(payload.decision_at)),
    end = Date.parse(String(payload.arrived_facility_at));
  return {
    points,
    decisionToArrivalMinutes:
      Number.isFinite(start) && Number.isFinite(end) && end >= start ? (end - start) / 60000 : null,
    issues: transportIssues(payload),
  };
}
export function TransportTimeline({
  events,
  episodeId,
  subjects,
  timeZone,
  onEdit,
}: {
  events: ClinicalEvent[];
  episodeId: string;
  subjects: ClinicalSubject[];
  timeZone: string;
  onEdit?: (event: ClinicalEvent) => void;
}) {
  const transports = activeBirthEvents(events, episodeId, new Date().toISOString()).filter(
    (e) => e.event_type === 'TRANSPORT',
  ) as ClinicalEvent[];
  return (
    <Card>
      <CardHeader>
        <CardTitle>Transport timeline</CardTitle>
        <CardDescription>
          Documented events only. Packet generation and any transmission require separate review.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {!transports.length && <p>No confirmed transport is documented.</p>}
        {transports.map((event) => {
          const timeline = transportTimeline(event.payload);
          return (
            <section key={event.id} className="flex flex-col gap-2 rounded border p-3">
              <h4 className="font-semibold">
                {subjects.find((s) => s.id === event.subject_id)?.label} · {String(event.payload.transport_type)}
              </h4>
              <ol className="flex flex-wrap gap-4">
                {timeline.points.map((point, index) => (
                  <li key={index}>
                    <span className="block font-medium">{point.label}</span>
                    {new Date(point.at).toLocaleString(undefined, { timeZone })}
                  </li>
                ))}
              </ol>
              <p>
                Decision to arrival:{' '}
                {timeline.decisionToArrivalMinutes === null
                  ? 'Not documented'
                  : `${timeline.decisionToArrivalMinutes.toFixed(1)} minutes`}
              </p>
              {timeline.issues.map((issue) => (
                <p key={issue} role="alert" className="text-destructive">
                  {issue}
                </p>
              ))}
              {onEdit && (
                <Button type="button" variant="outline" className="min-h-11 self-start" onClick={() => onEdit(event)}>
                  Review / update transport
                </Button>
              )}
            </section>
          );
        })}
      </CardContent>
    </Card>
  );
}
